import { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../lib/api';
import { siteUrl, siteByKey } from '../lib/sites';

// ── Customer requests (admin) ──
//
// The queue behind the portal's three ask-us-something forms. Before this
// existed all three only sent an email, so there was no list to work, no status
// to report back, and a deleted email was a lost request. The emails still go
// out exactly as before — this is the durable copy.
//
// Worked top-down: New → In progress → Done. Service requests sort to the top
// of the New count in the operator's head for a reason (a café with a dead
// grinder isn't serving coffee), so they carry the loudest chip.

const RED = '#A50B18';

const STATUS_LABELS = { new: 'New', in_progress: 'In progress', done: 'Done' };
const TYPE_LABELS = { service: 'Service', product: 'Product', feedback: 'Feedback' };

// Field order per type, so the detail panel reads the way the form was filled
// rather than however the object happened to serialize.
const FIELD_ORDER = {
  service: [
    ['equipment', 'Equipment'], ['issue', 'Issue'], ['urgency', 'Urgency'],
    ['preferredDate', 'Preferred date'], ['contactPhone', 'Contact phone'],
    ['additionalNotes', 'Notes'],
  ],
  product: [
    ['productDescription', 'Product'], ['brand', 'Brand'], ['quantity', 'Quantity'],
    ['requiredByDate', 'Required by'], ['additionalNotes', 'Notes'],
  ],
  feedback: [['topic', 'Topic'], ['message', 'Message']],
};

function whenText(iso) {
  if (!iso) return '—';
  const then = new Date(iso);
  if (isNaN(then)) return '—';
  const mins = Math.floor((Date.now() - then.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 8) return `${days}d ago`;
  return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function exactWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d) ? '' : d.toLocaleString('en-US', { timeZone: 'America/Denver' });
}

export default function RequestsTab({ readOnly }) {
  const [requests, setRequests] = useState([]);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState('new');
  const [type, setType] = useState('');
  const [openId, setOpenId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.listRequests({ status, type });
      setRequests(data.requests || []);
      setCounts(data.counts || {});
    } catch (e) {
      setError(e.message || 'Could not load requests.');
    } finally {
      setLoading(false);
    }
  }, [status, type]);

  useEffect(() => { load(); }, [load]);

  async function move(request, next) {
    setSaving(request.requestId);
    setError('');
    try {
      await adminApi.setRequestStatus(request.requestId, next);
      await load();
    } catch (e) {
      setError(e.message || 'Could not update that request.');
    } finally {
      setSaving(null);
    }
  }

  const newCount = counts.new || 0;

  return (
    <div>
      <div style={s.headerRow}>
        <div>
          <h3 style={s.title}>Requests</h3>
          <p style={s.sub}>
            Service, product and feedback submissions from the portal.
            {newCount > 0
              ? ` ${newCount} waiting.`
              : ' Nothing waiting.'}
          </p>
        </div>
        <button style={s.btnOutline} onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {error && <div style={s.errorBanner}>{error}</div>}

      <div style={s.serviceNotice}>
        Service requests are now worked in the <a href={siteUrl(siteByKey('service'))} style={{ color: RED, fontWeight: 600 }}>Service desk</a>, where each one becomes a job for a technician. Product requests and feedback stay here.
      </div>

      <div style={s.filters}>
        <div style={s.filterGroup}>
          {['new', 'in_progress', 'done', ''].map(value => (
            <button
              key={value || 'all'}
              onClick={() => setStatus(value)}
              style={value === status ? s.filterOn : s.filterOff}
            >
              {value ? STATUS_LABELS[value] : 'All'}
              {value && counts[value] != null ? ` (${counts[value]})` : ''}
            </button>
          ))}
        </div>
        <div style={s.filterGroup}>
          {['', 'service', 'product', 'feedback'].map(value => (
            <button
              key={value || 'all-types'}
              onClick={() => setType(value)}
              style={value === type ? s.filterOn : s.filterOff}
            >
              {value ? TYPE_LABELS[value] : 'All types'}
            </button>
          ))}
        </div>
      </div>

      {loading && !requests.length && <p style={s.sub}>Loading requests…</p>}

      {!loading && !requests.length && (
        <div style={s.card}>
          <p style={s.sub}>
            Nothing here. Requests appear the moment a customer submits the service,
            product or feedback form in the portal.
          </p>
        </div>
      )}

      {requests.map(request => {
        const open = openId === request.requestId;
        const fields = request.fields || {};
        return (
          <div key={request.requestId} style={s.card}>
            <div style={s.row}>
              <div style={s.rowMain}>
                <div style={s.rowName}>
                  <span style={request.type === 'service' ? s.chipService : s.chipType}>
                    {TYPE_LABELS[request.type] || request.type}
                  </span>
                  <span>{request.customerName || 'Unknown customer'}</span>
                  {request.emailSent === false && (
                    <span style={s.chipWarn} title="Stored, but the notification email did not send">
                      email failed
                    </span>
                  )}
                </div>
                <div style={s.rowMeta}>
                  {request.summary || '—'}
                </div>
                <div style={s.rowMeta}>
                  {request.submittedByEmail || '—'} · <span title={exactWhen(request.createdAt)}>{whenText(request.createdAt)}</span>
                  {request.status !== 'new' && ` · ${STATUS_LABELS[request.status] || request.status}`}
                  {request.updatedBy && request.status !== 'new' && ` by ${request.updatedBy}`}
                </div>
              </div>
              <div style={s.actions}>
                <button
                  style={s.btnOutline}
                  onClick={() => setOpenId(open ? null : request.requestId)}
                >
                  {open ? 'Hide' : 'Details'}
                </button>
                {!readOnly && request.status === 'new' && (
                  <button style={s.btnPrimary} disabled={saving === request.requestId}
                    onClick={() => move(request, 'in_progress')}>
                    {saving === request.requestId ? 'Saving…' : 'Start'}
                  </button>
                )}
                {!readOnly && request.status === 'in_progress' && (
                  <button style={s.btnPrimary} disabled={saving === request.requestId}
                    onClick={() => move(request, 'done')}>
                    {saving === request.requestId ? 'Saving…' : 'Mark done'}
                  </button>
                )}
                {!readOnly && request.status === 'done' && (
                  <button style={s.btnOutline} disabled={saving === request.requestId}
                    onClick={() => move(request, 'new')}>
                    Reopen
                  </button>
                )}
              </div>
            </div>

            {open && (
              <div style={s.detail}>
                {(FIELD_ORDER[request.type] || []).map(([key, label]) => (
                  fields[key] ? (
                    <div key={key} style={s.detailRow}>
                      <div style={s.detailLabel}>{label}</div>
                      <div style={s.detailValue}>{fields[key]}</div>
                    </div>
                  ) : null
                ))}
                <div style={s.detailRow}>
                  <div style={s.detailLabel}>Submitted</div>
                  <div style={s.detailValue}>{exactWhen(request.createdAt)}</div>
                </div>
                <div style={s.detailRow}>
                  <div style={s.detailLabel}>Customer ID</div>
                  <div style={s.detailValue}>{request.customerId || '—'}</div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const s = {
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem', gap: '1rem', flexWrap: 'wrap' },
  title: { fontSize: '1.1rem', fontWeight: 700, color: '#222', margin: '0 0 0.25rem' },
  sub: { color: '#888', fontSize: '0.85rem', margin: 0 },
  filters: { display: 'flex', gap: '1.25rem', flexWrap: 'wrap', marginBottom: '1rem' },
  serviceNotice: { background: '#fff8e6', border: '1px solid #f3dca5', borderRadius: 8, padding: '0.6rem 0.9rem', fontSize: '0.85rem', marginBottom: '1rem' },
  filterGroup: { display: 'flex', gap: '0.35rem', flexWrap: 'wrap' },
  filterOn: { background: RED, color: '#fff', border: `1px solid ${RED}`, borderRadius: 8, padding: '0.35rem 0.75rem', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' },
  filterOff: { background: 'transparent', color: '#555', border: '1px solid #ddd', borderRadius: 8, padding: '0.35rem 0.75rem', fontSize: '0.8rem', cursor: 'pointer' },
  card: { background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: '0.9rem 1.1rem', marginBottom: '0.75rem' },
  row: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.6rem' },
  rowMain: { flex: 1, minWidth: 240 },
  rowName: { fontSize: '0.92rem', fontWeight: 600, color: '#222', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' },
  rowMeta: { fontSize: '0.8rem', color: '#888', marginTop: '0.2rem' },
  actions: { display: 'flex', gap: '0.4rem', flexWrap: 'wrap' },
  chipType: { background: '#eef2ff', color: '#3340a0', borderRadius: 6, padding: '0.1rem 0.4rem', fontSize: '0.7rem', fontWeight: 700 },
  chipService: { background: '#fdecea', color: RED, borderRadius: 6, padding: '0.1rem 0.4rem', fontSize: '0.7rem', fontWeight: 700 },
  chipWarn: { background: '#fff3e0', color: '#b26500', borderRadius: 6, padding: '0.1rem 0.4rem', fontSize: '0.7rem', fontWeight: 700 },
  btnPrimary: { background: RED, color: '#fff', border: 'none', borderRadius: 8, padding: '0.45rem 0.9rem', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' },
  btnOutline: { background: 'transparent', color: '#555', border: '1px solid #ddd', borderRadius: 8, padding: '0.45rem 0.9rem', fontSize: '0.8rem', cursor: 'pointer' },
  errorBanner: { background: '#fdecea', border: '1px solid #f5c6cb', borderRadius: 8, padding: '0.6rem 1rem', marginBottom: '1rem', fontSize: '0.85rem', color: '#a32d2d' },
  detail: { marginTop: '0.85rem', paddingTop: '0.85rem', borderTop: '1px solid #f3f3f3', display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  detailRow: { display: 'flex', gap: '0.75rem', alignItems: 'flex-start', flexWrap: 'wrap' },
  detailLabel: { fontSize: '0.78rem', fontWeight: 600, color: '#555', minWidth: 110 },
  detailValue: { fontSize: '0.85rem', color: '#333', whiteSpace: 'pre-wrap', flex: 1, minWidth: 200 },
};
